import { useCallback } from 'react';
import { supabase } from '../lib/woocommerce';

// Helper function to convert base64 URL safe to Uint8Array for VAPID key
function urlBase64ToUint8Array(base64String: string) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/\-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function useNativePush() {
  const subscribeToPush = useCallback(async (storeSlug: string, role: string, userId?: string) => {
    try {
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        console.log('[Native Push] Web Push not supported by this browser.');
        return false;
      }

      // Check current permission
      let permission = Notification.permission;
      if (permission === 'default') {
        permission = await Notification.requestPermission();
      }

      if (permission !== 'granted') {
        console.log('[Native Push] Notification permission denied.');
        return false;
      }

      const registration = await navigator.serviceWorker.ready;
      
      const vapidPublicKey = import.meta.env.VITE_VAPID_PUBLIC_KEY;
      if (!vapidPublicKey) {
        console.error('[Native Push] VITE_VAPID_PUBLIC_KEY is not defined in env.');
        return false;
      }

      const convertedVapidKey = urlBase64ToUint8Array(vapidPublicKey);

      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: convertedVapidKey
      });

      const subJSON = subscription.toJSON();

      if (!subJSON.endpoint || !subJSON.keys?.p256dh || !subJSON.keys?.auth) {
        throw new Error('Invalid subscription payload');
      }

      // Save to Supabase (Shadow Mode)
      const { error } = await supabase
        .from('push_subscriptions')
        .upsert({
          endpoint: subJSON.endpoint,
          keys_p256dh: subJSON.keys.p256dh,
          keys_auth: subJSON.keys.auth,
          store_slug: storeSlug,
          role: role,
          user_id: userId
        }, { onConflict: 'endpoint' });

      if (error) {
        console.error('[Native Push] Failed to save subscription to Supabase:', error);
        return false;
      }

      console.log(`[Native Push] Subscribed successfully in Shadow Mode for ${storeSlug} (Role: ${role})`);
      return true;

    } catch (err) {
      console.error('[Native Push] Error during push subscription:', err);
      return false;
    }
  }, []);

  return { subscribeToPush };
}
